import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Form,
  Input,
  message,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {CloseOutlined, ExclamationCircleFilled, MailOutlined, SaveOutlined, SearchOutlined, UndoOutlined,} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import PageContainer from '../../components/common/PageContainer';
import {useTranslation} from '../../hooks/useTranslation';
import {useAppSelector} from '../../hooks/hooks';
import {selectUser} from '../../store/slices/SessionSlice';
import {
  MASTER_RECIPIENT_MODE,
  NotificationEvent,
  NotificationEventState,
  NotificationSettings,
  useNotificationSettings,
  useResetNotificationTemplate,
  useSaveNotificationSettings,
  useSaveNotificationTemplate,
} from '../../hooks/settings/useNotificationSettings';
import {extractErrorMessage, HTTP_STATUS, isHttpStatus} from '../../utils/apiError';
import {EMAIL_PATTERN, EMAIL_TOKEN_SEPARATORS} from '../../utils/validation';
import {layout, spacing} from '../../theme/tokens';
import './styles/notifications.less';

const {Text} = Typography;
const {CheckableTag} = Tag;

const KEYS = 'settings.content.notifications';

const TAB = {edit: 'edit', preview: 'preview'} as const;
const FIELD = {subject: 'subject', body: 'body'} as const;
const FILTER = {all: 'all', active: 'active', inactive: 'inactive', custom: 'custom'} as const;
const RECIPIENT = {master: 'master', cc: 'cc'} as const;

type FilterKey = (typeof FILTER)[keyof typeof FILTER];
type RecipientKind = (typeof RECIPIENT)[keyof typeof RECIPIENT];
type FormValues = Record<string, boolean | string | string[]>;

interface DrawerTarget {
  code: string;
  sectionCode: string;
}

interface TemplateDraft {
  subject: string;
  body: string;
}

interface RecipientChip {
  key: string;
  label: string;
  className: string;
  dot: boolean;
}

const FILTERS: FilterKey[] = Object.values(FILTER);
const MAX_RECIPIENTS_PER_FIELD = 20;
const TEMPLATE_BODY_ROWS = 12;
const PREVIEW_SAMPLE_TOKEN = 'incidente.codigo';
const EMAIL_SERVER_ROUTE = '/settings/email-server';

const optionKey = (code: string) => `${code}::option`;
const masterRecipientsKey = (code: string) => `${code}::masterRecipients`;
const extraRecipientsKey = (code: string) => `${code}::extraRecipients`;

const buildFormValues = (settings: NotificationSettings): FormValues => {
  const values: FormValues = {};
  settings.sections.forEach((section) =>
    section.events.forEach((event) => {
      values[event.code] = Boolean(event.enabled);
      if (event.option) {
        values[optionKey(event.code)] = event.option.value;
      }
      if (event.masterRecipientMode !== MASTER_RECIPIENT_MODE.none) {
        values[masterRecipientsKey(event.code)] = event.masterRecipients;
      }
      values[extraRecipientsKey(event.code)] = event.extraRecipients;
    }),
  );
  return values;
};

const allEvents = (settings: NotificationSettings | undefined): NotificationEvent[] =>
  settings ? settings.sections.flatMap((section) => section.events) : [];

const Notifications: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const user = useAppSelector(selectUser);

  const {data, isLoading, isError} = useNotificationSettings();
  const saveSettings = useSaveNotificationSettings();
  const saveTemplateMutation = useSaveNotificationTemplate();
  const resetTemplateMutation = useResetNotificationTemplate();

  const hydrated = useRef(false);
  const [saved, setSaved] = useState<FormValues>({});
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>(FILTER.all);

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [openRecipients, setOpenRecipients] = useState<Record<string, boolean>>({});
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const [drawer, setDrawer] = useState<DrawerTarget | null>(null);
  const [draft, setDraft] = useState<TemplateDraft>({subject: '', body: ''});
  const [tab, setTab] = useState<string>(TAB.edit);
  const [focusField, setFocusField] = useState<keyof TemplateDraft>(FIELD.body);

  useEffect(() => {
    if (!data || hydrated.current) return;
    hydrated.current = true;
    const values = buildFormValues(data);
    setSaved(values);
    form.setFieldsValue(values);
  }, [data, form]);

  const watched = Form.useWatch([], {form, preserve: true}) as FormValues | undefined;
  const values = watched ?? saved;
  const dirty = JSON.stringify(values) !== JSON.stringify(saved);
  const events = useMemo(() => allEvents(data), [data]);

  const translateOrFallback = (key: string, fallback: string): string => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const fieldLabel = (code: string) => translateOrFallback(`${KEYS}.fields.${code}.label`, code);
  const fieldDescription = (code: string) => translateOrFallback(`${KEYS}.fields.${code}.description`, code);
  const optionValueLabel = (value: string) => translateOrFallback(`${KEYS}.options.${value}`, value);
  const masterRecipientOverride = (code: string, field: 'description', generic: string) =>
    translateOrFallback(`${KEYS}.fields.${code}.masterRecipient.${field}`, generic);
  const tokenHint = (token: string) => translateOrFallback(`${KEYS}.template.vars.${token}.hint`, token);
  const tokenSample = (token: string) => translateOrFallback(`${KEYS}.template.vars.${token}.sample`, token);

  const masterRecipientStates = events
    .filter((event) => event.masterRecipientMode !== MASTER_RECIPIENT_MODE.none)
    .map((event) => {
      const key = masterRecipientsKey(event.code);
      const list = (values[key] as string[] | undefined) ?? [];
      const invalid = list.filter((email) => !EMAIL_PATTERN.test(email));
      const overCap = list.length > MAX_RECIPIENTS_PER_FIELD;
      const required = event.masterRecipientMode === MASTER_RECIPIENT_MODE.required && Boolean(values[event.code]);
      const complete = list.length > 0 && invalid.length === 0 && !overCap;
      return {
        event,
        key,
        list,
        invalid,
        overCap,
        valid: !overCap && (!required || complete),
        showError: (required || overCap) && !complete && Boolean(touched[key]),
      };
    });

  const blocking = masterRecipientStates.find((state) => !state.valid);

  // ----- Bulk save -----

  const handleSave = async () => {
    if (blocking) {
      setTouched((prev) => ({...prev, [blocking.key]: true}));
      setOpenRecipients((prev) => ({...prev, [blocking.event.code]: true}));
      return;
    }

    setSaveErrorMessage(null);
    try {
      const items: NotificationEventState[] = events.map((event) => ({
        code: event.code,
        enabled: Boolean(values[event.code]),
        optionValue: event.option ? (values[optionKey(event.code)] as string) : null,
        extraRecipients: (values[extraRecipientsKey(event.code)] as string[] | undefined) ?? [],
        masterRecipients:
          event.masterRecipientMode !== MASTER_RECIPIENT_MODE.none
            ? (values[masterRecipientsKey(event.code)] as string[] | undefined) ?? []
            : [],
      }));

      const refreshed = await saveSettings.mutateAsync(items);
      const refreshedValues = buildFormValues(refreshed);
      setSaved(refreshedValues);
      form.setFieldsValue(refreshedValues);
      void messageApi.success(t(`${KEYS}.messages.saved`));
    } catch (error) {
      const text = extractErrorMessage(error, t(`${KEYS}.messages.saveError`));
      setSaveErrorMessage(text);
      void messageApi.error(text);
    }
  };

  const handleDiscard = () => {
    form.setFieldsValue(saved);
    setTouched({});
    setSaveErrorMessage(null);
    void messageApi.info(t(`${KEYS}.messages.reset`));
  };

  // ----- Template drawer -----

  const drawerEvent = drawer ? events.find((event) => event.code === drawer.code) ?? null : null;
  const drawerSection = drawer ? data?.sections.find((section) => section.code === drawer.sectionCode) : undefined;
  const templateLocale = data?.defaultLocale ?? '';
  const drawerTemplate = drawerEvent?.templates.find((tpl) => tpl.locale === templateLocale) ?? null;

  const openTemplate = (event: NotificationEvent, sectionCode: string) => {
    if (!data) return;
    const template = event.templates.find((tpl) => tpl.locale === data.defaultLocale);
    setDrawer({code: event.code, sectionCode});
    setDraft({subject: template?.subject ?? '', body: template?.body ?? ''});
    setTab(TAB.edit);
    setFocusField(FIELD.body);
  };

  const closeTemplate = () => {
    setDrawer(null);
    setDraft({subject: '', body: ''});
  };

  const updateDraft = (field: keyof TemplateDraft, value: string) => {
    setDraft((prev) => ({...prev, [field]: value}));
  };

  const handleSaveTemplate = async () => {
    if (!drawer) return;
    try {
      const version = drawerTemplate?.custom ? drawerTemplate.version : null;
      const updated = await saveTemplateMutation.mutateAsync({
        code: drawer.code,
        locale: templateLocale,
        payload: {subject: draft.subject, body: draft.body, version},
      });
      const fresh = updated.templates.find((tpl) => tpl.locale === templateLocale);
      if (fresh) {
        setDraft({subject: fresh.subject, body: fresh.body});
      }
      void messageApi.success(t(`${KEYS}.template.messages.saved`));
    } catch (error) {
      if (isHttpStatus(error, HTTP_STATUS.CONFLICT)) {
        void messageApi.error(t(`${KEYS}.template.messages.conflict`));
      } else {
        void messageApi.error(extractErrorMessage(error, t(`${KEYS}.template.messages.saveError`)));
      }
    }
  };

  const handleResetTemplate = async () => {
    if (!drawer) return;
    try {
      const updated = await resetTemplateMutation.mutateAsync({code: drawer.code, locale: templateLocale});
      const fresh = updated.templates.find((tpl) => tpl.locale === templateLocale);
      if (fresh) {
        setDraft({subject: fresh.subject, body: fresh.body});
      }
      void messageApi.info(t(`${KEYS}.template.messages.reset`));
    } catch (error) {
      void messageApi.error(extractErrorMessage(error, t(`${KEYS}.template.messages.saveError`)));
    }
  };

  const insertVariable = (token: string) => {
    const wrapped = `{{${token}}}`;
    setDraft((prev) => {
      const currentValue = prev[focusField];
      const separator = currentValue.length && !/\s$/.test(currentValue) ? ' ' : '';
      return {...prev, [focusField]: currentValue + separator + wrapped};
    });
  };

  const fillSample = (text: string, tokens: string[]) =>
    tokens.reduce((acc, token) => acc.split(`{{${token}}}`).join(tokenSample(token)), text);

  // ----- Derived rows -----

  const normalizedQuery = query.trim().toLowerCase();

  const matchesQuery = (event: NotificationEvent) =>
    !normalizedQuery ||
    `${fieldLabel(event.code)} ${fieldDescription(event.code)}`.toLowerCase().includes(normalizedQuery);

  const matchesFilter = (event: NotificationEvent) => {
    const on = Boolean(values[event.code]);
    if (filter === FILTER.active) return on;
    if (filter === FILTER.inactive) return !on;
    if (filter === FILTER.custom) return event.custom;
    return true;
  };

  const visibleSections = (data?.sections ?? [])
    .map((section) => ({
      section,
      items: section.events.filter((event) => matchesQuery(event) && matchesFilter(event)),
    }))
    .filter((entry) => entry.items.length > 0);

  const totalItems = events.length;
  const totalActive = events.filter((event) => Boolean(values[event.code])).length;

  const countLabel = t(`${KEYS}.countActive`)
    .replace('{active}', String(totalActive))
    .replace('{total}', String(totalItems));

  // ----- Renderers -----

  const toggleRecipients = (event: NotificationEvent) => {
    const wasOpen = Boolean(openRecipients[event.code]);
    setOpenRecipients((prev) => ({...prev, [event.code]: !wasOpen}));
    if (wasOpen) {
      setTouched((prev) => ({
        ...prev,
        [masterRecipientsKey(event.code)]: true,
        [extraRecipientsKey(event.code)]: true,
      }));
    }
  };

  const recipientChips = (list: string[], kind: RecipientKind, required: boolean): RecipientChip[] => {
    const base =
      kind === RECIPIENT.cc ? 'gt-notif-recipient-chip gt-notif-recipient-chip--cc' : 'gt-notif-recipient-chip';

    if (list.length === 0) {
      const quiet = kind === RECIPIENT.cc || !required;
      return [
        {
          key: 'empty',
          label: t(`${KEYS}.recipients.${kind === RECIPIENT.cc ? 'emptyCc' : 'emptyMaster'}`),
          className: `${base} gt-notif-recipient-chip--${quiet ? 'quiet' : 'invalid'} gt-notif-recipient-chip--empty`,
          dot: !quiet,
        },
      ];
    }

    return list.map((email) => {
      const invalid = !EMAIL_PATTERN.test(email);
      return {
        key: email,
        label: email,
        className: invalid ? `${base} gt-notif-recipient-chip--invalid` : base,
        dot: invalid && kind === RECIPIENT.master,
      };
    });
  };

  const renderChip = (chip: RecipientChip) => (
    <Tag key={chip.key} className={chip.className}>
      {chip.dot && <Badge status="error"/>}
      {chip.label}
    </Tag>
  );

  const renderRecipients = (event: NotificationEvent) => {
    const master = masterRecipientStates.find((entry) => entry.event.code === event.code);
    const masterRequired = event.masterRecipientMode === MASTER_RECIPIENT_MODE.required;
    const extraKey = extraRecipientsKey(event.code);
    const extraList = (values[extraKey] as string[] | undefined) ?? [];
    const extraInvalid = extraList.filter((email) => !EMAIL_PATTERN.test(email));
    const extraShowError = extraInvalid.length > 0 && Boolean(touched[extraKey]);
    const open = Boolean(openRecipients[event.code]);
    const editorId = `${event.code}-recipients-editor`;

    return (
      <>
        <div className="gt-notif-recipient-summary">
          {master && (
            <>
              <span className="gt-notif-hint">{t(`${KEYS}.recipients.master`)}</span>
              {recipientChips(master.list, RECIPIENT.master, masterRequired).map(renderChip)}
              <Divider orientation="vertical"/>
            </>
          )}

          <span className="gt-notif-hint">{t(`${KEYS}.recipients.cc`)}</span>
          {recipientChips(extraList, RECIPIENT.cc, false).map(renderChip)}

          <Button
            type="link"
            size="small"
            className="gt-notif-recipient-edit"
            aria-expanded={open}
            aria-controls={editorId}
            onClick={() => toggleRecipients(event)}
          >
            {t(`${KEYS}.recipients.${open ? 'done' : 'edit'}`)}
          </Button>
        </div>

        {open && (
          <div
            id={editorId}
            className={
              master
                ? 'gt-notif-recipient-editor'
                : 'gt-notif-recipient-editor gt-notif-recipient-editor--single'
            }
          >
            {master && (
              <div className="gt-notif-recipient-field">
                <span className="gt-notif-recipient-label">
                  {t(`${KEYS}.masterRecipient.label`)}
                  {masterRequired && <span className="gt-notif-required">*</span>}
                </span>

                <Form.Item name={master.key} noStyle>
                  <Select<string[]>
                    mode="tags"
                    allowClear
                    open={false}
                    status={master.showError ? 'error' : undefined}
                    tokenSeparators={EMAIL_TOKEN_SEPARATORS}
                    placeholder={t(`${KEYS}.masterRecipient.placeholder`)}
                    options={[]}
                    onBlur={() => setTouched((prev) => ({...prev, [master.key]: true}))}
                    onChange={(next) => {
                      if (next.length > MAX_RECIPIENTS_PER_FIELD) {
                        form.setFieldValue(master.key, next.slice(0, MAX_RECIPIENTS_PER_FIELD));
                        void messageApi.warning(
                          t(`${KEYS}.masterRecipient.validation.tooMany`).replace(
                            '{max}',
                            String(MAX_RECIPIENTS_PER_FIELD),
                          ),
                        );
                      }
                    }}
                  />
                </Form.Item>

                {master.showError && (
                  <Text type="danger" className="gt-notif-error">
                    <ExclamationCircleFilled/>
                    {master.list.length === 0
                      ? t(`${KEYS}.masterRecipient.validation.required`)
                      : master.overCap
                        ? t(`${KEYS}.masterRecipient.validation.tooMany`).replace(
                          '{max}',
                          String(MAX_RECIPIENTS_PER_FIELD),
                        )
                        : t(`${KEYS}.masterRecipient.validation.invalid`).replace(
                          '{emails}',
                          master.invalid.join(', '),
                        )}
                  </Text>
                )}
              </div>
            )}

            <div className="gt-notif-recipient-field">
              <span className="gt-notif-recipient-label">
                {t(`${KEYS}.recipients.cc`)}
                <span className="gt-notif-optional">{t(`${KEYS}.recipients.optional`)}</span>
              </span>

              <Form.Item name={extraKey} noStyle>
                <Select<string[]>
                  mode="tags"
                  allowClear
                  open={false}
                  status={extraShowError ? 'error' : undefined}
                  tokenSeparators={EMAIL_TOKEN_SEPARATORS}
                  placeholder={t(`${KEYS}.extraRecipients.placeholder`)}
                  options={[]}
                  onBlur={() => setTouched((prev) => ({...prev, [extraKey]: true}))}
                  onChange={(next) => {
                    if (next.length > MAX_RECIPIENTS_PER_FIELD) {
                      form.setFieldValue(extraKey, next.slice(0, MAX_RECIPIENTS_PER_FIELD));
                      void messageApi.warning(
                        t(`${KEYS}.extraRecipients.tooMany`).replace('{max}', String(MAX_RECIPIENTS_PER_FIELD)),
                      );
                    }
                  }}
                />
              </Form.Item>

              {extraShowError && (
                <Text type="danger" className="gt-notif-error">
                  <ExclamationCircleFilled/>
                  {t(`${KEYS}.extraRecipients.invalid`).replace('{emails}', extraInvalid.join(', '))}
                </Text>
              )}
            </div>

            <Text type="secondary" className="gt-notif-hint">
              {masterRecipientOverride(event.code, 'description', t(`${KEYS}.recipients.note`))}
            </Text>
          </div>
        )}
      </>
    );
  };

  const renderRow = (event: NotificationEvent, sectionCode: string) => {
    const on = Boolean(values[event.code]);

    return (
      <div key={event.code} className={on ? 'gt-notif-row' : 'gt-notif-row gt-notif-row--off'}>
        <div className="gt-notif-row-main">
          <div className="gt-notif-title">{fieldLabel(event.code)}</div>
          <div className="gt-notif-hint">{fieldDescription(event.code)}</div>

          {on && renderRecipients(event)}

          {event.option && on && (
            <div className="gt-notif-sub">
              <div className="gt-notif-row-main">
                <div>{fieldLabel(event.option.code)}</div>
                <div className="gt-notif-hint">{fieldDescription(event.option.code)}</div>
              </div>
              <Form.Item name={optionKey(event.code)} noStyle>
                <Select
                  size="small"
                  className="gt-notif-sub-select"
                  options={event.option.allowedValues.map((value) => ({
                    label: optionValueLabel(value),
                    value,
                  }))}
                />
              </Form.Item>
            </div>
          )}
        </div>

        <Space size={spacing.x12} className="gt-notif-row-actions">
          {event.custom && <Tag className="gt-notif-tag-custom">{t(`${KEYS}.template.custom`)}</Tag>}
          {on && event.hasTemplate ? (
            <Button
              type="link"
              size="small"
              icon={<MailOutlined/>}
              className="gt-notif-link"
              onClick={() => openTemplate(event, sectionCode)}
            >
              {t(`${KEYS}.template.configure`)}
            </Button>
          ) : (
            <Text type="secondary" className="gt-notif-nowrap">{t(`${KEYS}.template.none`)}</Text>
          )}
          <Form.Item name={event.code} valuePropName="checked" noStyle>
            <Switch
              checkedChildren={t(`${KEYS}.switch.on`)}
              unCheckedChildren={t(`${KEYS}.switch.off`)}
            />
          </Form.Item>
        </Space>
      </div>
    );
  };

  const renderEditTab = () => {
    if (!data || !drawerEvent) return null;
    return (
      <div className="gt-notif-drawer-panel">
        <Space orientation="vertical" size={spacing.x20} style={{width: '100%'}}>
          <div>
            <Text className="gt-notif-label">{t(`${KEYS}.template.subject`)}</Text>
            <Input
              value={draft.subject}
              placeholder={t(`${KEYS}.template.subjectPlaceholder`)}
              onFocus={() => setFocusField(FIELD.subject)}
              onChange={(event) => updateDraft(FIELD.subject, event.target.value)}
            />
          </div>

          <div>
            <Text className="gt-notif-label">{t(`${KEYS}.template.body`)}</Text>
            <Input.TextArea
              value={draft.body}
              rows={TEMPLATE_BODY_ROWS}
              className="gt-notif-body-input"
              onFocus={() => setFocusField(FIELD.body)}
              onChange={(event) => updateDraft(FIELD.body, event.target.value)}
            />
          </div>

          <div>
            <Space size={spacing.x8} align="baseline" style={{marginBottom: spacing.x8}} wrap>
              <Text className="gt-notif-label">{t(`${KEYS}.template.variables.title`)}</Text>
              <span className="gt-notif-hint">
                {t(`${KEYS}.template.variables.hint`).replace(
                  '{target}',
                  t(`${KEYS}.template.variables.${focusField}`),
                )}
              </span>
            </Space>
            <Space size={spacing.x6} wrap>
              {drawerEvent.templateTokens.map((token) => (
                <Tooltip key={token} title={tokenHint(token)}>
                  <Tag className="gt-notif-var" onClick={() => insertVariable(token)}>
                    {`{{${token}}}`}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </div>
        </Space>
      </div>
    );
  };

  const renderPreviewTab = () => {
    if (!drawerEvent) return null;
    return (
      <div className="gt-notif-drawer-panel">
        <Space orientation="vertical" size={spacing.x12} style={{width: '100%'}}>
          <span className="gt-notif-hint">
            {t(`${KEYS}.template.preview.note`).replace('{code}', tokenSample(PREVIEW_SAMPLE_TOKEN))}
          </span>
          <div className="gt-notif-preview">
            <div className="gt-notif-preview-head">
              <div className="gt-notif-preview-row">
                <span className="gt-notif-preview-label">{t(`${KEYS}.template.preview.from`)}</span>
                <span>{t(`${KEYS}.template.preview.sender`)}</span>
              </div>
              <div className="gt-notif-preview-row">
                <span className="gt-notif-preview-label">{t(`${KEYS}.template.preview.to`)}</span>
                <span>{user?.email || t(`${KEYS}.template.preview.recipient`)}</span>
              </div>
              <div className="gt-notif-preview-row">
                <span className="gt-notif-preview-label">{t(`${KEYS}.template.preview.subject`)}</span>
                <span className="gt-notif-title">{fillSample(draft.subject, drawerEvent.templateTokens)}</span>
              </div>
            </div>
            <div className="gt-notif-preview-body">
              {fillSample(draft.body, drawerEvent.templateTokens)}
            </div>
          </div>
        </Space>
      </div>
    );
  };

  return (
    <PageContainer
      title={t(`${KEYS}.title`)}
      subTitle={t(`${KEYS}.description`)}
      className="gt-notif-page"
      titleClassName="h3 gt-text-color-neutral-800"
      headerPaddingBottom={true}
      extra={
        <Space size={spacing.x12}>
          <span className={blocking ? 'gt-notif-hint gt-notif-hint--error' : 'gt-notif-hint'}>
            {blocking
              ? t(`${KEYS}.dirty.invalidRecipient`).replace('{field}', fieldLabel(blocking.event.code))
              : t(`${KEYS}.dirty.${dirty ? 'unsaved' : 'saved'}`)}
          </span>
          <Button icon={<UndoOutlined/>} onClick={handleDiscard} disabled={!dirty || saveSettings.isPending}>
            {t(`${KEYS}.buttons.cancel`)}
          </Button>
          {/* Tooltip needs a wrapper: a disabled Button swallows its own pointer events. */}
          <Tooltip title={blocking ? t(`${KEYS}.dirty.saveBlocked`) : ''}>
            <span>
              <Button
                type="primary"
                icon={<SaveOutlined/>}
                loading={saveSettings.isPending}
                disabled={!dirty || Boolean(blocking)}
                onClick={handleSave}
              >
                {t(`${KEYS}.buttons.save`)}
              </Button>
            </span>
          </Tooltip>
        </Space>
      }
    >
      {contextHolder}
      <Spin spinning={isLoading}>
        <div className="gt-notif">
          {isError && <Alert type="error" showIcon title={t(`${KEYS}.messages.loadError`)}/>}

          {saveErrorMessage && (
            <Alert
              type="error"
              showIcon
              closable={{onClose: () => setSaveErrorMessage(null)}}
              title={saveErrorMessage}
            />
          )}

          {data && !data.emailServerConfigured && (
            <Alert
              type="warning"
              showIcon
              title={t(`${KEYS}.banner.emailNotConfigured`)}
              action={
                <Button size="small" onClick={() => navigate(EMAIL_SERVER_ROUTE)}>
                  {t(`${KEYS}.banner.configureAction`)}
                </Button>
              }
            />
          )}

          <Form form={form} component={false} initialValues={saved} preserve>
            <div className="gt-notif-toolbar">
              <Input
                className="gt-notif-search"
                allowClear
                prefix={<SearchOutlined/>}
                placeholder={t(`${KEYS}.searchPlaceholder`)}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <Space size={spacing.x6} wrap>
                {FILTERS.map((key) => (
                  <CheckableTag
                    key={key}
                    className="gt-notif-chip"
                    checked={filter === key}
                    onChange={() => setFilter(key)}
                  >
                    {t(`${KEYS}.filters.${key}`)}
                  </CheckableTag>
                ))}
              </Space>
              <span className="gt-notif-hint gt-notif-hint--end">{countLabel}</span>
            </div>

            {visibleSections.map(({section, items}) => {
              const activeCount = section.events.filter((event) => values[event.code]).length;
              const sectionCount = t(`${KEYS}.sections.count`)
                .replace('{active}', String(activeCount))
                .replace('{total}', String(section.events.length));

              return (
                <Card
                  key={section.code}
                  size="small"
                  className="gt-notif-section"
                  title={
                    <Space size={spacing.x12} align="center">
                      <span className={`gt-notif-badge gt-notif-badge--${section.tone}`}>
                        {t(`${KEYS}.sections.${section.code}.abbr`)}
                      </span>
                      <span className="gt-notif-title">
                        {t(`${KEYS}.sections.${section.code}.title`)}
                      </span>
                      <Tag className="gt-notif-pill">{sectionCount}</Tag>
                    </Space>
                  }
                  extra={
                    <span className="gt-notif-hint">
                      {t(`${KEYS}.sections.${section.code}.description`)}
                    </span>
                  }
                >
                  {items.map((event) => renderRow(event, section.code))}
                </Card>
              );
            })}

            {!isLoading && visibleSections.length === 0 && (
              <div className="gt-notif-empty">{t(`${KEYS}.empty`)}</div>
            )}
          </Form>
        </div>
      </Spin>

      <Drawer
        open={Boolean(drawer)}
        size={layout.drawerWidthWide}
        closable={false}
        mask={{closable: true}}
        className="gt-notif-drawer"
        onClose={closeTemplate}
        title={
          <div>
            <span className="gt-notif-eyebrow">{t(`${KEYS}.template.eyebrow`)}</span>
            <div className="gt-notif-drawer-title">{drawerEvent ? fieldLabel(drawerEvent.code) : ''}</div>
            <span className="gt-notif-hint">
              {drawerSection ? t(`${KEYS}.sections.${drawerSection.code}.title`) : ''}
            </span>
          </div>
        }
        extra={<Button type="text" icon={<CloseOutlined/>} onClick={closeTemplate}/>}
        footer={
          <div className="gt-notif-drawer-footer">
            <Button type="link" loading={resetTemplateMutation.isPending} onClick={handleResetTemplate}>
              {t(`${KEYS}.template.buttons.reset`)}
            </Button>
            <Space size={spacing.x8}>
              <Button onClick={closeTemplate}>{t(`${KEYS}.template.buttons.cancel`)}</Button>
              <Button type="primary" loading={saveTemplateMutation.isPending} onClick={handleSaveTemplate}>
                {t(`${KEYS}.template.buttons.save`)}
              </Button>
            </Space>
          </div>
        }
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {key: TAB.edit, label: t(`${KEYS}.template.tabs.edit`), children: renderEditTab()},
            {key: TAB.preview, label: t(`${KEYS}.template.tabs.preview`), children: renderPreviewTab()},
          ]}
        />
      </Drawer>
    </PageContainer>
  );
};

export default Notifications;
